import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { OtpInputState } from '@voyagu/portal-design-system';
import { interval, map, Observable, Subject, takeUntil } from 'rxjs';

import { PortalUserVerificationNode } from '@app/services/api/api.types';
import { intervalMs } from '@app/utils/constants';
import { bindOtpDigitDataIds } from '@app/utils/otp-digit-data-ids.util';
import { getDomain } from '@app/utils/utils';

@Component({
  selector: 'verification-modal',
  templateUrl: './verification-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerificationModalComponent implements OnInit, OnChanges, AfterViewInit {
  hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  domain: { label: string; url: string } | null;
  form = new FormGroup({
    code: new FormControl('', { nonNullable: true }),
  });

  timer$: Observable<number>;

  loading: boolean;

  @Input() verification: PortalUserVerificationNode;
  @Input() verificationPassed: boolean;
  @Input() verificationError: string | null;

  @Output() hideModal = new EventEmitter();
  @Output() validateCode = new EventEmitter();
  @Output() portalSendVerificationEmail = new EventEmitter();

  get otpState(): OtpInputState {
    if (this.verificationPassed) {
      return 'success';
    }

    if (this.form.disabled) {
      return 'disabled';
    }

    return 'default';
  }

  ngOnInit() {
    this.domain = getDomain(this.verification?.emailMasked);

    this.startCountdown();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (typeof changes['verificationError']?.currentValue === 'string') {
      this.form.enable();
      this.form.controls.code.setValue('');
      this.focusFirstInput();
    }

    if (changes['verificationPassed']?.currentValue === true) {
      setTimeout(() => this.hideModal.emit(), 1000);
    }
  }

  ngAfterViewInit() {
    bindOtpDigitDataIds(this.hostElement.nativeElement, 'digit');
    this.focusFirstInput();
  }

  onCompleted(code: string) {
    if (this.form.disabled || code.length !== 4) {
      return;
    }

    this.form.disable();
    this.validateCode.emit(code);
  }

  sendVerificationEmail() {
    if (!this.loading) {
      this.portalSendVerificationEmail.emit();
    }

    this.loading = true;
  }

  startCountdown() {
    const destroy$ = new Subject<void>();
    const emailSentAt = this.verification.emailSentAt;
    const startTime = emailSentAt
      ? new Date(`${this.verification.emailSentAt.split('+')[0]}+00:00`).getTime() // todo fix after be
      : new Date().getTime();
    const endTime = startTime + intervalMs;

    this.timer$ = interval(1000)
      .pipe(takeUntil(destroy$))
      .pipe(
        map(() => {
          const now = new Date().getTime();

          const currentTime = endTime - now;

          if (currentTime <= 0) {
            setTimeout(() => {
              destroy$.next();
              destroy$.complete();
            });
          }

          return currentTime;
        }),
      );
  }

  focusFirstInput() {
    setTimeout(() => {
      bindOtpDigitDataIds(this.hostElement.nativeElement, 'digit');
      this.hostElement.nativeElement.querySelector<HTMLElement>('.otp-input__cell')?.focus();
    });
  }
}
